import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
export const SessionContext = createContext(null);
/** The session: status, the `AppPrincipal` (an authz principal + display data), and every auth flow. */
export function useSession() {
    const ctx = useContext(SessionContext);
    if (!ctx)
        throw new Error('useSession must be used inside <SessionProvider>');
    return ctx;
}
/**
 * Holds the session for the whole app: restores it on mount, exposes every auth flow and
 * tenant switching, and hands the bearer token to the API clients. The adapter decides how
 * (mock fixtures or Cognito SRP) — nothing below this provider knows which.
 */
export function SessionProvider({ adapter, hooks = {}, children, }) {
    const [status, setStatus] = useState('loading');
    const [principal, setPrincipalState] = useState(null);
    const principalRef = useRef(null);
    const hooksRef = useRef(hooks);
    useEffect(() => {
        hooksRef.current = hooks;
    }, [hooks]);
    const setPrincipal = useCallback((p) => {
        principalRef.current = p;
        setPrincipalState(p);
    }, []);
    const enrich = useCallback(async (p, reason) => {
        const after = hooksRef.current.afterSignIn;
        if (!after)
            return p;
        try {
            return await after(p, reason);
        }
        catch {
            return p;
        }
    }, []);
    const settle = useCallback(async (step) => {
        if (step.kind !== 'done')
            return step;
        const p = await enrich(step.principal, 'sign-in');
        setPrincipal(p);
        setStatus('authenticated');
        return { kind: 'done', principal: p };
    }, [enrich, setPrincipal]);
    useEffect(() => {
        let cancelled = false;
        adapter
            .restore()
            .then(async (p) => {
            if (cancelled)
                return;
            if (!p) {
                setPrincipal(null);
                setStatus('anonymous');
                return;
            }
            const enriched = await enrich(p, 'restore');
            if (cancelled)
                return;
            setPrincipal(enriched);
            setStatus('authenticated');
        })
            .catch(() => {
            if (cancelled)
                return;
            setPrincipal(null);
            setStatus('anonymous');
        });
        return () => {
            cancelled = true;
        };
    }, [adapter, enrich, setPrincipal]);
    const signIn = useCallback((input) => adapter.signIn(input).then(settle), [adapter, settle]);
    const signInAsFixture = useCallback((id) => adapter.signInAsFixture(id).then(settle), [adapter, settle]);
    const completeNewPassword = useCallback((pw) => adapter.completeNewPassword(pw).then(settle), [adapter, settle]);
    const confirmTotp = useCallback((code) => adapter.confirmTotp(code).then(settle), [adapter, settle]);
    const beginTotpSetup = useCallback(() => adapter.beginTotpSetup(), [adapter]);
    const confirmTotpSetup = useCallback((code) => adapter.confirmTotpSetup(code).then(settle), [adapter, settle]);
    const forgotPassword = useCallback((email) => adapter.forgotPassword(email), [adapter]);
    const resetPassword = useCallback((input) => adapter.resetPassword(input), [adapter]);
    const changePassword = useCallback((input) => adapter.changePassword(input), [adapter]);
    const signUp = useCallback((input) => adapter.signUp(input), [adapter]);
    const confirmSignUp = useCallback((input) => adapter.confirmSignUp(input), [adapter]);
    const resendSignUpCode = useCallback((email) => adapter.resendSignUpCode(email), [adapter]);
    const refresh = useCallback(async () => {
        const p = await adapter.refresh();
        if (!p) {
            setPrincipal(null);
            setStatus('anonymous');
            return null;
        }
        const enriched = await enrich(p, 'refresh');
        setPrincipal(enriched);
        setStatus('authenticated');
        return enriched;
    }, [adapter, enrich, setPrincipal]);
    const signOut = useCallback(async () => {
        setPrincipal(null);
        setStatus('anonymous');
        await adapter.signOut();
    }, [adapter, setPrincipal]);
    const switchTenant = useCallback(async (tenantId) => {
        const p = await adapter.switchTenant(tenantId);
        const enriched = await enrich(p, 'switch');
        setPrincipal(enriched);
        return enriched;
    }, [adapter, enrich, setPrincipal]);
    const getAccessToken = useCallback(() => adapter.getAccessToken(), [adapter]);
    const getPrincipal = useCallback(() => principalRef.current, []);
    const updatePrincipal = useCallback((p) => setPrincipal(p), [setPrincipal]);
    const value = useMemo(() => ({
        status,
        principal,
        mode: adapter.mode,
        fixtures: adapter.fixtures(),
        signIn,
        signInAsFixture,
        completeNewPassword,
        confirmTotp,
        beginTotpSetup,
        confirmTotpSetup,
        forgotPassword,
        resetPassword,
        changePassword,
        signUp,
        confirmSignUp,
        resendSignUpCode,
        refresh,
        signOut,
        switchTenant,
        getAccessToken,
        getPrincipal,
        updatePrincipal,
    }), [
        status,
        principal,
        adapter,
        signIn,
        signInAsFixture,
        completeNewPassword,
        confirmTotp,
        beginTotpSetup,
        confirmTotpSetup,
        forgotPassword,
        resetPassword,
        changePassword,
        signUp,
        confirmSignUp,
        resendSignUpCode,
        refresh,
        signOut,
        switchTenant,
        getAccessToken,
        getPrincipal,
        updatePrincipal,
    ]);
    return _jsx(SessionContext.Provider, { value: value, children: children });
}
//# sourceMappingURL=session.js.map